import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import AccessTokenManager from '../services/accessTokenManager';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export const AdminPage: React.FC = () => {
  const [bidId, setBidId] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleResubmit = async () => {
    if (!bidId.trim()) {
      setMessage({ type: 'error', text: 'Please enter a Bid ID' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const token = AccessTokenManager.getAccessToken();
      if (!token) {
        throw new Error('No auth token available');
      }

      const response = await fetch(`${API_BASE_URL}/admin/bids/${bidId}/reprocess`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to reprocess bid' }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();
      setMessage({ type: 'success', text: result.message || 'Bid reprocessing initiated successfully' });
      setBidId('');
    } catch (error) {
      console.error('Error reprocessing bid:', error);
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to reprocess bid'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-4 sm:py-8 max-w-4xl">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Admin Panel</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">Administrative tools and controls</p>
      </div>

      {/* Resubmit Bid Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <RefreshCw className="h-4 w-4 sm:h-5 sm:w-5" />
            Reprocess Bid
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs sm:text-sm text-gray-600">
            Enter a Bid ID to reprocess the bid extraction and analysis.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={bidId}
              onChange={(e) => setBidId(e.target.value)}
              placeholder="Enter Bid ID (UUID)"
              className="flex-1 px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
            <Button
              onClick={handleResubmit}
              disabled={loading || !bidId.trim()}
              className="gap-2 w-full sm:w-auto whitespace-nowrap"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="hidden sm:inline">Processing...</span>
                  <span className="sm:hidden">Processing</span>
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Reprocess
                </>
              )}
            </Button>
          </div>

          {message && (
            <div
              className={`flex items-start gap-2 p-3 rounded-md ${
                message.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              {message.type === 'error' && <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 mt-0.5" />}
              <p className="text-xs sm:text-sm break-words">{message.text}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
